import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import { makeStyles, Typography } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { HorizontalStackedBar } from '../../../../../../../components/molecules/charts/HorizontalStackedBar';
import { CardStatPanel } from '../../../../../../../components/organims/cards/CardStatPanel';
import { palette } from '../../../../../../../settings/themes/colors';

const CHART_RIGHT_MARGIN = 55;
const BAR_COLOR = palette.azure;

const useStyles = makeStyles({
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: palette.dimGray,
    letterSpacing: 0.8,
    textTransform: 'uppercase'
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 900,
    color: palette.dimGray
  },
  axisRuler: {
    flexShrink: 0,
    marginTop: 4,
    borderTop: `1px solid ${palette.platinum}`,
    paddingTop: 2
  }
});

const buildChartData = (data, dimensionKey) => (data || []).map(item => ({ ...item, displayLabel: item[dimensionKey] }));

export const BarWidget = ({ widget }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [chartConfig, setChartConfig] = useState(null);

  const { title, dimension, series, data } = widget;
  const dimensionKey = (dimension && dimension.key) || 'label';

  const chartSeries = (series || []).map(s => ({
    key: camelCase(s.key),
    label: s.label,
    stackId: 'a',
    color: s.color || BAR_COLOR
  }));
  const chartData = buildChartData(data, dimensionKey);
  const unit = (data || []).map(item => item.unit).find(u => u != null) || null;

  const renderFooter = () => {
    if (!chartConfig) return null;
    const { grandTotal, ticks, maxTick, yAxisWidth } = chartConfig;

    return (
      <>
        <div className={classes.footer}>
          <Typography className={classes.totalLabel}>
            <FormattedMessage id="common.total" />
          </Typography>
          <Typography className={classes.totalValue}>{intl.formatNumber(grandTotal)}</Typography>
        </div>
        <div className={classes.axisRuler}>
          <ResponsiveContainer width="100%" height={28}>
            <BarChart data={[{ displayLabel: '' }]} layout="vertical" margin={{ top: 0, right: CHART_RIGHT_MARGIN, left: 0, bottom: 4 }}>
              <XAxis
                type="number"
                ticks={ticks}
                domain={[0, maxTick]}
                tick={{ fontSize: 9, fill: palette.dimGray }}
                axisLine={{ stroke: palette.platinum }}
                tickLine={{ stroke: palette.platinum }}
              />
              <YAxis type="category" width={yAxisWidth} tick={false} axisLine={false} tickLine={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  };

  return (
    <CardStatPanel title={title} footer={renderFooter()}>
      <HorizontalStackedBar data={chartData} series={chartSeries} unit={unit} onReady={setChartConfig} />
    </CardStatPanel>
  );
};

BarWidget.propTypes = {
  widget: PropTypes.shape({
    title: PropTypes.string,
    dimension: PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string
    }),
    series: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string,
        color: PropTypes.string
      })
    ).isRequired,
    data: PropTypes.array.isRequired
  }).isRequired
};
